/**
 * Zentrale Konfigurationsdatei für alle Referenzen und Projekte
 */

export interface ReferenceProject {
  /**
   * Eindeutige ID des Projekts (wird für Filterung verwendet)
   */
  id: string;
  
  /**
   * Titel des Projekts
   */
  title: string;
  
  /**
   * Name des Unternehmens/Kunden
   */
  companyName: string;
  
  /**
   * Dateiname des Logos (in src/assets/images/references/logo/)
   */
  logo: string;
  
  /**
   * Array von Dateinamen für Galerie-Bilder (in src/assets/images/references/gallery/)
   * Falls nicht angegeben, wird nur das Logo angezeigt
   */
  gallery?: string[];
  
  /**
   * Tags für Filterung
   * z.B. 'ERP', 'E-Commerce', 'Shopware', 'Business Apps', 'Schnittstellen'
   */
  tags: string[];
  
  /**
   * Kurzbeschreibung des Projekts (mehrsprachig)
   */
  description?: {
    de: string;
    en: string;
  };
  
  /**
   * Die Herausforderung des Projekts (mehrsprachig)
   */
  challenge?: {
    de: string;
    en: string;
  };
  
  /**
   * Die umgesetzte Lösung (mehrsprachig)
   */
  solution?: {
    de: string;
    en: string;
  };
  
  /**
   * Kundenmeinung (Testimonial)
   */
  testimonial?: {
    /**
     * Das Zitat der Kundenmeinung (mehrsprachig)
     */
    quote: {
      de: string;
      en: string;
    };
    /**
     * Der Name der Person
     */
    personName: string;
    /**
     * Position/Titel der Person
     */
    personTitle?: string;
    /**
     * Firmenname (falls abweichend vom companyName)
     */
    companyName?: string;
  };
  
  /**
   * Website des Kunden (falls vorhanden)
   */
  website?: string;
  
  /**
   * Benutzerdefinierter Text für den Website-Button (mehrsprachig)
   * Wird standardmäßig "Zur Website" bzw. "Visit Website" angezeigt
   * @deprecated Verwende stattdessen buttons für mehr Flexibilität
   */
  buttonText?: {
    de: string;
    en: string;
  };
  
  /**
   * Array von Buttons für unterschiedliche Links und Aktionen
   */
  buttons?: Array<{
    /**
     * Text für den Button (mehrsprachig)
     */
    text: {
      de: string;
      en: string;
    };
    /**
     * URL für den Button-Link
     */
    url: string;
    /**
     * Icon-Name oder Art des Buttons 
     * z.B. 'website', 'app-store', 'google-play', 'download', etc.
     */
    icon?: string;
    /**
     * Zusätzliche Farbklasse für den Button
     * z.B. 'blue', 'green', etc.
     */
    color?: string;
  }>;
  
  /**
   * Ob das Projekt hervorgehoben werden soll (z.B. für Features)
   */
  featured?: boolean;
}

/**
 * Liste aller Referenzprojekte
 */
export const referenceProjects: ReferenceProject[] = [
  {
    id: 'eventbranchenverzeichnis',
    title: 'Branchenportal für die Eventbranche',
    companyName: 'Eventbranchenverzeichnis',
    logo: 'eventbranchenverzeichnis.png',
    gallery: ['eventbranchenverzeichnis-1.png', 'eventbranchenverzeichnis-2.png'],
    tags: ['Portal', 'Wartung', 'Migration', 'SEO', 'API'],
    challenge: {
      de: 'Das größte Dienstleisterportal der Eventbranche mit 21.000 Einträgen und über 400 Kategorien musste auf eine moderne Umgebung migriert werden – SEO-konform und mit Beibehaltung der Online-Buchungsstrecke und des Self-Service-Portals für Anbieter.',
      en: 'The largest service provider portal in the event industry with 21,000 entries and over 400 categories needed to be migrated to a modern environment - SEO-compliant and maintaining the online booking process and self-service portal for providers.'
    },
    solution: {
      de: 'Weiterführung der Migration auf ein modernes NodeJs Backend, Einführung leistungsfähiger REST-APIs zur Performance-Optimierung der Webseite. Umzug von Google Server zu einer self-hosted Umgebung zur Kosteneinsparung. Kontinuierliche Weiterentwicklung und Wartung des Portals sowie Umsetzung wirksamer SEO-Maßnahmen.',
      en: 'Continuation of migration to a modern NodeJs backend, introduction of powerful REST APIs to optimize website performance. Migration from Google Server to a self-hosted environment to save costs. Continuous development and maintenance of the portal and implementation of effective SEO measures.'
    },
    website: 'https://eventbranchenverzeichnis.de',
    buttonText: {
      de: 'Portal besuchen',
      en: 'Visit Portal'
    },
    featured: true
  },
  {
    id: 'eventnetzwerker',
    title: 'Eventnetzwerker App',
    companyName: 'memo-media Verlags-GmbH',
    logo: 'eventnetzwerker.webp',
    gallery: ['eventnetzwerker-1.png', 'eventnetzwerker-2.png'],
    tags: ['App-Entwicklung', 'Mobile', 'React Native', 'Node.js', 'Echtzeit'],
    challenge: {
      de: 'Unübersichtliche Terminkalender, unbeständige Kontakte und das permanente Gefühl, wichtige Programmpunkte auf Events und Messen zu verpassen, zählt zu den immer wiederkehrenden Herausforderungen der meisten Eventakteur:innen. Es fehlte eine zentrale Plattform zur Vernetzung.',
      en: 'Confusing appointment calendars, inconsistent contacts, and the constant feeling of missing important program items at events and trade fairs are among the recurring challenges faced by most event professionals. A central networking platform was missing.'
    },
    solution: {
      de: 'Wir haben die Eventnetzwerker-App und ein dazugehöriges Admin-Center entwickelt. Die App bietet ausgefeilte Messefunktionen mit detaillierten Informationen zu Ausstellern, Programmpunkten, Spielorten, Neuigkeiten und Speakern. Nutzer können persönliche Highlights markieren und eigene Messe-ToDos erstellen. Das integrierte Networking-System mit Chat-Funktionen und Goodies-Übersicht sorgt für direkten Austausch zwischen allen Teilnehmern. Technisch umgesetzt als hybride App für iOS und Android mit einem zentralen Backend.',
      en: 'We developed the Eventnetzwerker app and an accompanying admin center. The app offers sophisticated trade fair functions with detailed information on exhibitors, program items, venues, news and speakers. Users can mark personal highlights and create their own trade fair to-dos. The integrated networking system with chat functions and goodies overview enables direct exchange between all participants. Technically implemented as a hybrid app for iOS and Android with a central backend.'
    },
    description: {
      de: 'Das technische Herzstück ist ein leistungsstarkes Admin-Center, das es Eventveranstaltern ermöglicht, alle Messedaten mit wenigen Klicks zu pflegen oder per Schnittstelle aus Drittsystemen zu importieren. Die App wurde als modulare Hybrid-Anwendung mit React Native und einem Node.js-Backend entwickelt, um maximale Plattformkompatibilität bei gleichzeitig nativer Benutzererfahrung zu bieten. Besonders anspruchsvoll war die Entwicklung der Offline-Funktionalität und die Echtzeit-Synchronisation zwischen allen Geräten.',
      en: 'The technical centerpiece is a powerful admin center that allows event organizers to maintain all trade fair data with just a few clicks or import it from third-party systems via interfaces. The app was developed as a modular hybrid application with React Native and a Node.js backend to offer maximum platform compatibility with a native user experience. The development of offline functionality and real-time synchronization between all devices was particularly challenging.'
    },
    testimonial: {
      quote: {
        de: "Die App 'Eventnetzwerker' bringt auf einer zentralen Oberfläche zusammen, was zusammengehört. Professionelle Anbieter der Eventbranche, Fachmessen, Multiplikatoren-Events und Eventplanende.",
        en: "The 'Eventnetzwerker' app brings together what belongs together on a central interface. Professional providers in the event industry, trade fairs, multiplier events, and event planners."
      },
      personName: 'Kerstin Meisner-Schaul',
      personTitle: 'Geschäftsführerin',
      companyName: 'memo-media Verlags-GmbH'
    },
    website: 'https://eventnetzwerker.de',
    buttons: [
      {
        text: {
          de: 'iOS App herunterladen',
          en: 'Download iOS App'
        },
        url: 'https://apps.apple.com/app/eventnetzwerker/id123456789',
        icon: 'apple',
        color: 'blue'
      },
      {
        text: {
          de: 'Android App herunterladen',
          en: 'Download Android App'
        },
        url: 'https://play.google.com/store/apps/details?id=de.eventnetzwerker.app',
        icon: 'android',
        color: 'green'
      },
      {
        text: {
          de: 'Website besuchen',
          en: 'Visit Website'
        },
        url: 'https://eventnetzwerker.de',
        icon: 'website'
      }
    ],
    featured: true
  },
  {
    id: 'wolframberge',
    title: 'B2C Shop für Feinschmecker',
    companyName: 'Wolfram Berge Delikatessen',
    logo: 'wolframberge.png',
    gallery: ['wolframberge-1.png', 'wolframberge-2.png'],
    tags: ['E-Commerce', 'Online-Marketing', 'Migration', 'Shopware 6', 'SEO'],
    challenge: {
      de: 'Der traditionsreiche Familienbetrieb wollte seinen Online-Shop von Shopware 5 auf Shopware 6 aktualisieren und gleichzeitig ein neues, frisches, aber konversionsstarkes Design umsetzen, ohne den persönlichen Charakter zu verlieren.',
      en: 'The traditional family business wanted to update their online shop from Shopware 5 to Shopware 6 and at the same time implement a new, fresh, but conversion-strong design without losing the personal character.'
    },
    solution: {
      de: 'Wir haben die Migration von Shopware 5 auf Shopware 6 erfolgreich durchgeführt und ein neues, konversionsstarkes Design entwickelt. Seitdem sorgen wir für regelmäßige jährliche Updates und kontinuierliche Optimierungen. Die Umstellung auf Shopware 6 konnte SEO und Umsatz deutlich steigern, und zusätzlich haben professionelle SEA-Kampagnen zu einem deutlichen Umsatzplus beigetragen.',
      en: 'We successfully migrated from Shopware 5 to Shopware 6 and developed a new, conversion-strong design. Since then, we have been providing regular annual updates and continuous optimizations. The switch to Shopware 6 has significantly improved SEO and sales, and professional SEA campaigns have additionally contributed to a significant increase in turnover.'
    },
    website: 'https://www.delikatessen-berge-shop.de',
    buttons: [
      {
        text: {
          de: 'Shop besuchen',
          en: 'Visit Shop'
        },
        url: 'https://www.delikatessen-berge-shop.de',
        icon: 'website'
      }
    ],
    featured: true
  },
  {
    id: 'goforartists',
    title: 'Künstlervermittlungs-Plattform',
    companyName: 'GoFor!Artists',
    logo: 'goforartists.webp',
    gallery: ['goforartists-1.png', 'goforartists-2.png'],
    tags: ['Plattform-Entwicklung', 'MVP'],
    challenge: {
      de: 'Die Gründer Kerstin und Jens wollten eine moderne Plattform für Künstlerbooking entwickeln, wussten aber nicht, wie sie schnell am Markt starten können.',
      en: 'The founders Kerstin and Jens wanted to develop a modern platform for artist booking, but didn\'t know how to quickly launch it in the market.'
    },
    solution: {
      de: 'Mit einem MVP-Ansatz haben wir binnen weniger Wochen die erste Version live geschaltet. Durch agiles Vorgehen konnten wir schnell mit der Zielgruppe weitere Funktionen entwickeln und die Plattform kontinuierlich verbessern.',
      en: 'With an MVP approach, we launched the first version within a few weeks. Through agile methodology, we were able to quickly develop additional features with the target group and continuously improve the platform.'
    },
    testimonial: {
      quote: {
        de: 'Die schnelle Umsetzung unserer Idee hat uns begeistert. Mittlerweile beherbergt unsere Plattform über 500 Angebote und mehr als 200 Künstler.',
        en: 'The quick implementation of our idea impressed us. Our platform now hosts over 500 offers and more than 200 artists.'
      },
      personName: 'Jens Müller',
      personTitle: 'Co-Founder',
      companyName: 'GoFor!Artists'
    },
    website: 'https://goforartists.com',
    buttonText: {
      de: 'Zur Plattform',
      en: 'Visit Platform'
    },
    featured: true
  },
  {
    id: 'memo-media',
    title: 'Digitalisierung und System-Integration',
    companyName: 'memo-media',
    logo: 'memo-media.png',
    gallery: ['memo-media-1.png', 'memo-media-2.png'],
    tags: ['Digitalisierung', 'Integration'],
    challenge: {
      de: 'Als Verlag stand memo-media vor der Herausforderung, veraltete Systeme zu modernisieren und Prozesse zu digitalisieren.',
      en: 'As a publisher, memo-media faced the challenge of modernizing outdated systems and digitizing processes.'
    },
    solution: {
      de: 'Wir unterstützen als Digitalisierungsagentur bei der Migration alter Systeme, integrieren Hubspot, entwickeln neue Buchungsstrecken, optimieren das Online-Marketing und haben die IT-Infrastruktur stets im Blick.',
      en: 'As a digitization agency, we support the migration of legacy systems, integrate Hubspot, develop new booking processes, optimize online marketing and always keep an eye on the IT infrastructure.'
    },
    testimonial: {
      quote: {
        de: 'Die Unterstützung bei unserer digitalen Transformation hat uns geholfen, effizienter zu arbeiten und neue Zielgruppen zu erschließen.',
        en: 'The support with our digital transformation has helped us to work more efficiently and reach new target groups.'
      },
      personName: 'Kerstin Schmidt',
      personTitle: 'Digital Manager',
      companyName: 'memo-media'
    },
    website: 'https://memo-media.de',
    buttonText: {
      de: 'Portal entdecken',
      en: 'Discover Portal'
    },
    featured: true
  },
  {
    id: 'voi',
    title: 'ERP-Implementation & Prozessoptimierung',
    companyName: 'voi leather design',
    logo: 'voi.svg',
    gallery: ['voi-1.png', 'voi-2.png'],
    tags: ['ERP', 'Digitalisierung'],
    challenge: {
      de: 'Das alte ERP-System von voi leather design war veraltet und bot nicht die nötige Flexibilität für modernes Geschäftsmanagement.',
      en: 'The old ERP system of voi leather design was outdated and did not offer the necessary flexibility for modern business management.'
    },
    solution: {
      de: 'Nach einer Evaluierungsphase haben wir Microsoft Business Central ERP eingeführt, das seitdem mit klarem Reporting und guter Benutzerfreundlichkeit überzeugt.',
      en: 'After an evaluation phase, we introduced Microsoft Business Central ERP, which has since convinced with clear reporting and good user-friendliness.'
    },
    testimonial: {
      quote: {
        de: 'Das neue Business Central System hat unsere internen Abläufe deutlich verbessert und bietet uns endlich die Transparenz, die wir für fundierte Geschäftsentscheidungen benötigen.',
        en: 'The new Business Central system has significantly improved our internal processes and finally gives us the transparency we need for well-founded business decisions.'
      },
      personName: 'Thomas Voigt',
      personTitle: 'COO',
      companyName: 'voi leather design'
    },
    website: 'https://voi.de',
    buttonText: {
      de: 'Shop besuchen',
      en: 'Visit Shop'
    },
    featured: true
  },
  {
    id: 'rautec',
    title: 'Strategische IT-Beratung & Digitalisierung',
    companyName: 'RAUTEC GmbH',
    logo: 'rautec.png',
    gallery: ['rautec-1.png', 'rautec-2.png'],
    tags: ['Beratung', 'IT-Strategie'],
    challenge: {
      de: 'RAUTEC GmbH suchte einen strategischen Partner für die digitale Transformation, der alle IT- und Digitalfragen kompetent beantwortet.',
      en: 'RAUTEC GmbH was looking for a strategic partner for digital transformation who could competently answer all IT and digital questions.'
    },
    solution: {
      de: 'Als Digitalisierungspartner beraten wir regelmäßig zu allen IT- und Digitalfragen. Als Sparringspartner diskutieren wir über die nächsten Schritte und finden die effektivste Lösung - sei es durch eigene Umsetzung, lokale Dienstleister oder unsere Expertise.',
      en: 'As a digitization partner, we regularly advise on all IT and digital issues. As a sparring partner, we discuss the next steps and find the most effective solution - whether through our own implementation, local service providers or our expertise.'
    },
    testimonial: {
      quote: {
        de: 'Die strategische Beratung hilft uns, die richtigen technologischen Entscheidungen zu treffen und unsere Ressourcen optimal einzusetzen.',
        en: 'The strategic consulting helps us to make the right technological decisions and to use our resources optimally.'
      },
      personName: 'Michael Rauch',
      personTitle: 'Geschäftsführer',
      companyName: 'RAUTEC GmbH'
    },
    website: 'https://rautec.de',
    featured: false
  },
  {
    id: 'wto',
    title: 'Individualsoftware für Freigabeprozesse',
    companyName: 'WTO Werbetechnik GmbH',
    logo: 'wto.png',
    gallery: ['wto-1.png', 'wto-2.png'],
    tags: ['Individualsoftware', 'Prozessoptimierung'],
    challenge: {
      de: 'WTO Werbetechnik brauchte eine effiziente Lösung für die Freigabe von Print-Produkten, um Prozesse zu vereinfachen und sich am Markt zu differenzieren.',
      en: 'WTO Werbetechnik needed an efficient solution for the approval of print products to simplify processes and differentiate itself in the market.'
    },
    solution: {
      de: 'Wir entwickeln Individualsoftware für die Freigabe von Print-Produkten. Dadurch wird der Prozess deutlich vereinfacht und WTO positioniert sich mit kluger eigener Software als Unternehmen mit Alleinstellungsmerkmal, was Kundenzufriedenheit und -bindung erhöht.',
      en: 'We develop custom software for the approval of print products. This significantly simplifies the process and with smart proprietary software, WTO positions itself as a company with a unique selling proposition, which increases customer satisfaction and loyalty.'
    },
    testimonial: {
      quote: {
        de: 'Die entwickelte Software hat nicht nur unsere internen Prozesse optimiert, sondern wird von unseren Kunden als echter Mehrwert wahrgenommen.',
        en: 'The developed software has not only optimized our internal processes, but is perceived by our customers as real added value.'
      },
      personName: 'Stefan Weber',
      personTitle: 'Technischer Leiter',
      companyName: 'WTO Werbetechnik GmbH'
    },
    website: 'https://wto.de',
    buttonText: {
      de: 'Fallstudie lesen',
      en: 'Read Case Study'
    },
    featured: false
  }
];

/**
 * Verfügbare Tags für die Filterung
 */
export const availableTags = [
  'App-Entwicklung',
  'Mobile',
  'E-Commerce', 
  'Online-Marketing',
  'ERP', 
  'Digitalisierung',
  'Integration',
  'Individualsoftware',
  'Prozessoptimierung',
  'Plattform-Entwicklung',
  'MVP',
  'Portal',
  'Wartung',
  'Beratung',
  'IT-Strategie'
];

/**
 * Hole nur die Logos für das Logo-Carousel
 */
export const getCarouselLogos = () => {
  return referenceProjects
    .filter(project => project.featured !== false) // Nehme alle featured und undefined
    .map(project => project.logo);
};

/**
 * Filtere Projekte nach Tags und optionaler Textsuche
 * 
 * @param selectedTags Array mit ausgewählten Tags
 * @param searchTerm Optionaler Suchbegriff für die Textsuche
 * @returns Gefilterte Projekte
 */
export const filterProjectsByTags = (selectedTags: string[], searchTerm?: string) => {
  let filteredProjects = referenceProjects;
  
  // Filter nach Tags
  if (selectedTags.length > 0) {
    filteredProjects = filteredProjects.filter(project => 
      selectedTags.some(tag => project.tags.includes(tag))
    );
  }
  
  // Filter nach Textsuche, wenn ein Suchbegriff vorhanden ist
  if (searchTerm && searchTerm.trim() !== '') {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    
    filteredProjects = filteredProjects.filter(project => 
      // Suche in Titel
      project.title.toLowerCase().includes(normalizedSearchTerm) ||
      // Suche im Firmennamen
      project.companyName.toLowerCase().includes(normalizedSearchTerm) ||
      // Suche in Tags
      project.tags.some(tag => tag.toLowerCase().includes(normalizedSearchTerm)) ||
      // Suche in Beschreibung, wenn vorhanden
      (project.description?.de && project.description.de.toLowerCase().includes(normalizedSearchTerm)) ||
      // Suche in Herausforderung, wenn vorhanden
      (project.challenge?.de && project.challenge.de.toLowerCase().includes(normalizedSearchTerm)) ||
      // Suche in Lösung, wenn vorhanden
      (project.solution?.de && project.solution.de.toLowerCase().includes(normalizedSearchTerm))
    );
  }
  
  return filteredProjects;
}; 